import { useState, useEffect, useCallback } from 'react'
import { X, Calendar, Trash2 } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import { useLabels } from '@/lib/terminology'

type EpisodeStatus = 'scheduled' | 'recorded' | 'in-production' | 'published' | 'cancelled'

interface EpisodeData {
  id: string
  number: number
  title: string
  description: string | null
  recording_date: string
  recording_time: string | null
  air_date: string
  status: string
  series_id?: string
}

interface EditEpisodeModalProps {
  isOpen: boolean
  onClose: () => void
  episode: EpisodeData
  onSuccess?: () => void
  onDeleted?: () => void
}

export function EditEpisodeModal({
  isOpen,
  onClose,
  episode,
  onSuccess,
  onDeleted,
}: EditEpisodeModalProps) {
  const modalKey = isOpen ? `${episode.id}-open` : 'closed'

  if (!isOpen) return null

  return (
    <EditEpisodeModalContent
      key={modalKey}
      episode={episode}
      onClose={onClose}
      onSuccess={onSuccess}
      onDeleted={onDeleted}
    />
  )
}

interface EditEpisodeModalContentProps {
  episode: EpisodeData
  onClose: () => void
  onSuccess?: () => void
  onDeleted?: () => void
}

function EditEpisodeModalContent({
  episode,
  onClose,
  onSuccess,
  onDeleted,
}: EditEpisodeModalContentProps) {
  const queryClient = useQueryClient()
  const supabase = createClient()
  const labels = useLabels()

  const [title, setTitle] = useState(episode.title)
  const [description, setDescription] = useState(episode.description || '')
  const [recordingDate, setRecordingDate] = useState(episode.recording_date)
  const [recordingTime, setRecordingTime] = useState(episode.recording_time || '')
  const [airDate, setAirDate] = useState(episode.air_date)
  const [status, setStatus] = useState<EpisodeStatus>(episode.status as EpisodeStatus)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const deleteEpisode = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('episodes')
        .delete()
        .eq('id', episode.id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['episodes'] })
      queryClient.invalidateQueries({ queryKey: ['recordings'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline'] })
      queryClient.invalidateQueries({ queryKey: ['schedule-episodes'] })
      onDeleted?.()
      onClose()
    },
  })

  const updateEpisode = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase
        .from('episodes')
        .update({
          title: title.trim(),
          description: description.trim() || null,
          recording_date: recordingDate,
          recording_time: recordingTime || null,
          air_date: airDate,
          status,
        } as never)
        .eq('id', episode.id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['episodes'] })
      queryClient.invalidateQueries({ queryKey: ['recordings'] })
      queryClient.invalidateQueries({ queryKey: ['schedule-episodes'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline'] })
      onSuccess?.()
      onClose()
    },
  })

  const isBusy = updateEpisode.isPending || deleteEpisode.isPending

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updateEpisode.mutate()
  }

  const handleClose = useCallback(() => {
    if (!isBusy) {
      onClose()
    }
  }, [isBusy, onClose])

  // Escape key handler for both the form and delete confirmation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showDeleteConfirm && !deleteEpisode.isPending) {
          setShowDeleteConfirm(false)
          deleteEpisode.reset()
        } else if (!isBusy) {
          onClose()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showDeleteConfirm, deleteEpisode, isBusy, onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop - prevent close during operations */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-md mx-4 bg-white dark:bg-zinc-900 rounded-xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            Edit Episode {episode.number}
          </h2>
          <button
            onClick={handleClose}
            disabled={isBusy}
            className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-zinc-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {/* Title */}
            <div>
              <label
                htmlFor="episode-title"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
              >
                Title <span className="text-rose-500">*</span>
              </label>
              <input
                id="episode-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Episode title"
                disabled={isBusy}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="episode-description"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
              >
                Description{' '}
                <span className="text-zinc-400 dark:text-zinc-500">(optional)</span>
              </label>
              <textarea
                id="episode-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is this episode about?"
                rows={3}
                disabled={isBusy}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 resize-none"
              />
            </div>

            {/* Status */}
            <div>
              <label
                htmlFor="episode-status"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
              >
                Status
              </label>
              <select
                id="episode-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as EpisodeStatus)}
                disabled={isBusy}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
              >
                <option value="scheduled">Scheduled</option>
                <option value="recorded">Recorded</option>
                <option value="in-production">In Production</option>
                <option value="published">Published</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="recording-date"
                  className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
                >
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Recording Date
                  </span>
                </label>
                <input
                  id="recording-date"
                  type="date"
                  value={recordingDate}
                  onChange={(e) => setRecordingDate(e.target.value)}
                  disabled={isBusy}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="air-date"
                  className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
                >
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Air Date
                  </span>
                </label>
                <input
                  id="air-date"
                  type="date"
                  value={airDate}
                  onChange={(e) => setAirDate(e.target.value)}
                  disabled={isBusy}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                  required
                />
              </div>
            </div>

            {/* Recording time */}
            <div>
              <label
                htmlFor="recording-time"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
              >
                Recording Time <span className="text-zinc-400 dark:text-zinc-500">(optional)</span>
              </label>
              <input
                id="recording-time"
                type="time"
                value={recordingTime}
                onChange={(e) => setRecordingTime(e.target.value)}
                disabled={isBusy}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
              />
            </div>

            {updateEpisode.isError && (
              <p className="text-sm text-rose-600 dark:text-rose-400">
                Failed to update episode. Please try again.
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-200 dark:border-zinc-800">
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isBusy}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-colors disabled:opacity-50"
              data-testid="delete-episode-button"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isBusy}
                className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!title.trim() || isBusy}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateEpisode.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-white dark:bg-zinc-900 rounded-xl flex flex-col items-center justify-center p-6 text-center">
            <Trash2 className="w-12 h-12 text-rose-500 mb-4" />
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
              Delete Episode {episode.number}?
            </h3>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6">
              This will permanently delete &quot;{episode.title}&quot; and all associated {labels.appearances.toLowerCase()}. This cannot be undone.
            </p>
            {deleteEpisode.isError && (
              <p className="text-sm text-rose-600 dark:text-rose-400 mb-4">
                Failed to delete episode. Please try again.
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false)
                  deleteEpisode.reset()
                }}
                disabled={deleteEpisode.isPending}
                className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteEpisode.mutate()}
                disabled={deleteEpisode.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors disabled:opacity-50"
                data-testid="confirm-delete-episode"
              >
                {deleteEpisode.isPending ? 'Deleting...' : 'Delete Episode'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
